import dayjs from "dayjs";
import { useEffect, useRef } from "react";

import { useOrganization } from "hooks/useOrganization";
import { Database } from "services/databases/useDatabasesNames";
import { isSystemDatabase } from "services/databases/utils";
import { WorkspaceEngine } from "services/engines/engine.types";
import { SqlRbacAction } from "services/rbac/action";
import { PrivilegeObjectType } from "services/rbac/types";
import { useCurrentUser } from "services/users/useCurrentUser";

import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import {
  ExecutionContext,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { useAccessManager } from "components/App/accessManager";

import { NONE } from "../DocumentContextSelector/types";

function isWithinLast10Minutes(timestamp: string): boolean {
  const givenTime = dayjs(timestamp);
  const minutesAgo = dayjs().diff(givenTime, "minutes");

  return minutesAgo <= 10;
}

export const useAutoSetContext = ({
  engines,
  databases,
  document,
}: {
  engines: WorkspaceEngine[];
  databases: Database[];
  document: WorkspaceDocument;
}) => {
  const { isAllowedTo } = useAccessManager();
  const currentUser = useCurrentUser();
  const {
    actions: { changeDocumentContext },
  } = useDocuments();
  const { data: organization } = useOrganization();
  const organizationCreatedWithinLast10Minutes = isWithinLast10Minutes(
    organization.created
  );

  const dependencies = useRef({
    id: document.id,
    engines,
    databases,
    engineContext: document.context.engine,
    databaseContext: document.context.database,
    changeDocumentContext,
    isAllowedTo,
    currentUser,
    organizationCreatedWithinLast10Minutes,
  });

  const getContextValues = () => {
    const { engines, databases, isAllowedTo, currentUser } =
      dependencies.current;
    const withoutSystem = engines.filter(
      engine => engine.engineName !== "system"
    );
    const getEngineContext = (options?: {
      selectFirstAvailableEngine?: boolean;
    }): Partial<ExecutionContext> => {
      if (currentUser?.defaultEngine) {
        const engine = engines.find(
          engine => engine.engineName === currentUser.defaultEngine
        );
        if (
          engine &&
          isAllowedTo(
            PrivilegeObjectType.engine,
            engine.engineName,
            SqlRbacAction.OPERATE,
            {
              owner: engine.engineOwner,
            }
          )
        ) {
          return { engine: { name: engine.engineName } };
        }
      }
      if (
        withoutSystem.length === 1 ||
        (options?.selectFirstAvailableEngine && withoutSystem.length > 0)
      ) {
        const engine = withoutSystem[0];
        if (
          isAllowedTo(
            PrivilegeObjectType.engine,
            engine.engineName,
            SqlRbacAction.OPERATE,
            {
              owner: engine.engineOwner,
            }
          )
        ) {
          return {
            engine: { name: engine.engineName },
          };
        }
      }
      return {};
    };

    const getDatabaseContext = (options?: {
      selectFirstAvailableDatabase?: boolean;
    }): Partial<ExecutionContext> => {
      const userDatabases = databases.filter(
        database => !isSystemDatabase(database.catalogName)
      );
      if (currentUser?.defaultDatabase) {
        const database = userDatabases.find(
          database => database.catalogName === currentUser.defaultDatabase
        );
        if (
          database &&
          isAllowedTo(
            PrivilegeObjectType.database,
            database.catalogName,
            SqlRbacAction.USAGE
          )
        ) {
          return { database: { name: database.catalogName } };
        }
      }
      if (
        userDatabases.length === 1 ||
        (options?.selectFirstAvailableDatabase && userDatabases.length > 0)
      ) {
        const database = userDatabases[0];
        if (
          isAllowedTo(
            PrivilegeObjectType.database,
            database.catalogName,
            SqlRbacAction.USAGE
          )
        ) {
          return { database: { name: database.catalogName } };
        }
      }
      return {};
    };

    return { getEngineContext, getDatabaseContext };
  };

  useEffect(() => {
    dependencies.current = {
      id: document.id,
      engines,
      databases,
      engineContext: document.context.engine,
      databaseContext: document.context.database,
      changeDocumentContext,
      isAllowedTo,
      currentUser,
      organizationCreatedWithinLast10Minutes,
    };
  }, [
    document,
    engines,
    databases,
    organizationCreatedWithinLast10Minutes,
    changeDocumentContext,
    isAllowedTo,
    currentUser,
  ]);

  useEffect(() => {
    const {
      id,
      engineContext,
      databaseContext,
      changeDocumentContext,
      organizationCreatedWithinLast10Minutes,
    } = dependencies.current;
    const update: Partial<ExecutionContext> = {};

    const { getEngineContext, getDatabaseContext } = getContextValues();

    if (organizationCreatedWithinLast10Minutes) return;

    if (
      !engineContext?.name ||
      (engineContext?.name === "system" && !engineContext?.isUserSelected)
    ) {
      Object.assign(update, getEngineContext());
    }

    if (
      !databaseContext?.name ||
      (databaseContext?.name === NONE && !databaseContext?.isUserSelected)
    ) {
      Object.assign(update, getDatabaseContext());
    }

    Object.keys(update).length && changeDocumentContext(id, update);
  }, []);

  // we want the first db and engine created by us to be autoselected for the first user
  useEffect(() => {
    const {
      id,
      engineContext,
      databaseContext,
      changeDocumentContext,
      databases,
    } = dependencies.current;

    const update: Partial<ExecutionContext> = {};
    const { getEngineContext, getDatabaseContext } = getContextValues();
    const userDatabases = databases.filter(
      database => !isSystemDatabase(database.catalogName)
    );

    if (!organizationCreatedWithinLast10Minutes) return;

    if (
      (!engineContext?.name ||
        (engineContext?.name === "system" && !engineContext?.isUserSelected)) &&
      engines.length >= 2
    ) {
      Object.assign(
        update,
        getEngineContext({ selectFirstAvailableEngine: true })
      );
    }

    if (
      (!databaseContext?.name ||
        (databaseContext?.name === NONE && !databaseContext?.isUserSelected)) &&
      userDatabases.length >= 1
    ) {
      Object.assign(
        update,
        getDatabaseContext({ selectFirstAvailableDatabase: true })
      );
    }

    Object.keys(update).length && changeDocumentContext(id, update);
  }, [
    databases.length,
    engines.length,
    organizationCreatedWithinLast10Minutes,
  ]);
};
